import { useCallback, useEffect, useState } from 'react';
import type { AuditRecord, OpsEvent } from '@urbanrats/ops-core';
import { ChangeRequests } from '../components/ChangeRequests.tsx';
import { DataTable } from '../components/DataTable.tsx';
import { PageHead, Section } from '../components/common.tsx';
import { actorName, CATEGORY, describe, isApproval, type AuditCategory } from '../lib/auditText.ts';
import { fmtTime, useCtx } from '../lib.ts';

type Rec = AuditRecord & { displayName?: string | null };
type Who = 'people' | 'system' | 'all';
interface ServerRow {
  seq: string;
  at: string;
  actor: string;
  role: string;
  action: string;
  target: string;
  reason: string;
  request_id: string | null;
  prev_hash: string;
  hash: string;
  display_name: string | null;
}

const PAGE = 300;
const dayOf = (iso: string) => new Date(iso).toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
const timeOf = (iso: string) => new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' });

/**
 * Audit log in plain language: who did what, when and why. People's actions are shown first (automatic
 * camera actions have their own filter); each record keeps its raw action code and the change request it
 * belongs to. The hash chain proves no record was edited or removed.
 */
export function Audit() {
  const { backend } = useCtx();
  const server = backend.server;
  const snap = backend.audit();
  const [who, setWho] = useState<Who>('people');
  const [cat, setCat] = useState<AuditCategory | 'all'>('all');
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [rows, setRows] = useState<Rec[] | null>(null);
  const [counts, setCounts] = useState<{ people24h: number; system24h: number } | null>(null);
  const [chain, setChain] = useState(snap.chainIntact);
  const [more, setMore] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const fetchPage = useCallback(
    async (before: number | null) => {
      if (!server) return null;
      const p = new URLSearchParams({ who, limit: String(PAGE) });
      if (before) p.set('before', String(before));
      if (q) p.set('q', q);
      const r = await server.get<{ chainIntact: boolean; records: ServerRow[]; counts: { people24h: number; system24h: number } }>(`/v1/admin/audit?${p}`);
      if (!r.ok) {
        setErr(r.error);
        return null;
      }
      setErr(null);
      setChain(r.value.chainIntact);
      setCounts(r.value.counts);
      setMore(r.value.records.length === PAGE);
      return r.value.records.map((x) => ({ seq: Number(x.seq), at: x.at, actor: x.actor, role: x.role, action: x.action, target: x.target, reason: x.reason, requestId: x.request_id, prevHash: x.prev_hash, hash: x.hash, displayName: x.display_name }));
    },
    [server, who, q],
  );
  useEffect(() => {
    if (!server) return;
    let live = true;
    const t = setTimeout(() => void fetchPage(null).then((r) => live && r && setRows(r)), 0);
    return () => {
      live = false;
      clearTimeout(t);
    };
  }, [server, fetchPage]);

  // Demo mode: the in-tab log (newest first), filtered the same way.
  const all: Rec[] = server ? (rows ?? []) : [...snap.records].reverse().filter((r) => (who === 'all' ? true : (who === 'system') === describe(r.action, r.actor).automatic) && (!q || JSON.stringify(r).toLowerCase().includes(q.toLowerCase())));
  const catCount = new Map<AuditCategory, number>();
  for (const r of all) {
    const c = describe(r.action, r.actor).cat;
    catCount.set(c, (catCount.get(c) ?? 0) + 1);
    if (isApproval(r.action) && c !== 'approvals') catCount.set('approvals', (catCount.get('approvals') ?? 0) + 1);
  }
  const shown = all.filter((r) => cat === 'all' || describe(r.action, r.actor).cat === cat || (cat === 'approvals' && isApproval(r.action)));
  const pending = backend.requests().filter((r) => r.status === 'PENDING');
  const days: [string, Rec[]][] = [];
  for (const r of shown) {
    const d = dayOf(r.at);
    const last = days.at(-1);
    if (last && last[0] === d) last[1].push(r);
    else days.push([d, [r]]);
  }

  return (
    <>
      <PageHead title="Audit log">Every change made in this portal — and every automatic action of our systems — is written here with who did it, when and why. Records can never be edited or deleted.</PageHead>

      <div className="au-summary">
        <div className={`au-card ${chain ? 'ok' : 'bad'}`} data-testid="audit-chain">
          <b>{chain ? '✓ Integrity check passed' : '✕ Integrity check FAILED'}</b>
          <span>{chain ? 'Hash chain intact: no record was changed or removed.' : 'Hash chain broken: a record was altered — escalate now.'}</span>
        </div>
        {counts && (
          <div className="au-card">
            <b>{counts.people24h}</b>
            <span>actions by people in the last 24 h</span>
          </div>
        )}
        {counts && (
          <div className="au-card">
            <b>{counts.system24h}</b>
            <span>automatic actions in the last 24 h</span>
          </div>
        )}
        <a className={`au-card ${pending.length ? 'warn' : ''}`} href="#approvals">
          <b>{pending.length}</b>
          <span>request{pending.length === 1 ? '' : 's'} waiting for approval →</span>
        </a>
      </div>

      <div className="au-filters">
        <div className="au-seg" role="radiogroup" aria-label="Who">
          {(
            [
              ['people', 'People'],
              ['system', 'Automatic'],
              ['all', 'Everything'],
            ] as [Who, string][]
          ).map(([k, l]) => (
            <button key={k} type="button" role="radio" aria-checked={who === k} className={who === k ? 'on' : ''} onClick={() => setWho(k)} data-testid={`audit-who-${k}`}>
              {l}
            </button>
          ))}
        </div>
        <form
          className="au-search"
          onSubmit={(e) => {
            e.preventDefault();
            setQ(search.trim());
          }}
        >
          <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search person, operator, camera, round, reason…" aria-label="Search the audit log" />
          <button type="submit" className="btn">
            Search
          </button>
          {q && (
            <button
              type="button"
              className="btn ghost"
              onClick={() => {
                setSearch('');
                setQ('');
              }}
            >
              Clear
            </button>
          )}
        </form>
      </div>
      <div className="au-chips" aria-label="Category">
        <button type="button" className={cat === 'all' ? 'on' : ''} onClick={() => setCat('all')}>
          All <span>{all.length}</span>
        </button>
        {(Object.keys(CATEGORY) as AuditCategory[])
          .filter((c) => catCount.get(c))
          .map((c) => (
            <button key={c} type="button" className={cat === c ? 'on' : ''} onClick={() => setCat(c)}>
              {CATEGORY[c].icon} {CATEGORY[c].label} <span>{catCount.get(c)}</span>
            </button>
          ))}
      </div>

      {err && (
        <p role="alert" className="field-error">
          {err}
        </p>
      )}
      <div className="au-list" data-testid="audit-table">
        {server && !rows ? (
          <p aria-busy="true">Loading…</p>
        ) : shown.length === 0 ? (
          <p className="empty">{who === 'people' ? 'No actions by people match. Try “Everything”.' : 'Nothing matches.'}</p>
        ) : (
          days.map(([d, list]) => (
            <section key={d} className="au-day">
              <h3>{d}</h3>
              <ul>
                {list.map((r) => {
                  const v = describe(r.action, r.actor);
                  return (
                    <li key={r.seq} className={`au-row ${v.automatic ? 'auto' : ''}`}>
                      <time dateTime={r.at} title={fmtTime(r.at)}>
                        {timeOf(r.at)}
                      </time>
                      <span className="au-ico" title={CATEGORY[v.cat].label} aria-hidden>
                        {CATEGORY[v.cat].icon}
                      </span>
                      <div className="au-main">
                        <p className="au-sentence">
                          <strong>{actorName(r.actor, r.displayName)}</strong> {v.phrase} <span className="mono">{r.target}</span>
                        </p>
                        {r.reason && <p className="au-reason">“{r.reason}”</p>}
                        <p className="au-meta">
                          <code>{r.action}</code> · #{r.seq} · {r.role}
                          {r.requestId ? ` · request ${r.requestId}` : ''}
                        </p>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))
        )}
      </div>
      {server && more && rows && (
        <button
          type="button"
          className="btn"
          onClick={async () => {
            const older = await fetchPage(rows.at(-1)?.seq ?? null);
            if (older) setRows([...rows, ...older]);
          }}
        >
          Show older records
        </button>
      )}

      <Section title="Approval requests (four-eyes)">
        <p className="note" id="approvals">
          Sensitive changes — refunds, voids, limits, keys, partner connections — are asked for by one person and approved by another. Each step also appears in the log above.
        </p>
        <h3>Waiting for a decision</h3>
        <ChangeRequests filter={(r) => r.status === 'PENDING'} testId="pending-requests" />
        <details>
          <summary>Decided requests</summary>
          <ChangeRequests filter={() => true} testId="all-requests" />
        </details>
      </Section>
      {!snap.serverSide && (
        <Section title="Events (demo stream)">
          <DataTable<OpsEvent>
            testId="events-table"
            rows={[...snap.events].reverse()}
            rowKey={(e) => String(e.seq)}
            empty="No events yet."
            columns={[
              { key: 'seq', header: '#', numeric: true, cell: (e) => e.seq },
              { key: 'at', header: 'At', cell: (e) => fmtTime(e.at) },
              { key: 'type', header: 'Type', cell: (e) => e.type },
              { key: 'target', header: 'Target', cell: (e) => e.target },
              { key: 'data', header: 'Data', cell: (e) => <span className="mono">{JSON.stringify(e.data)}</span> },
            ]}
          />
        </Section>
      )}
    </>
  );
}
